import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaPlay, FaTrash, FaDownload, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { SERVER_URL } from '../config';
import { AuthContext } from '../context/AuthContext';

// Import isProduction from config
const isProduction = window.location.hostname !== 'localhost';

const Recordings = () => {
  const { user } = useContext(AuthContext);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        if (!user || !user.username) {
          throw new Error('User not authenticated');
        }

        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(`${SERVER_URL}/recordings/user/${user.username}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch recordings');
        }
        
        const data = await response.json();
        
        // Process recordings to prepare thumbnails
        const processedRecordings = data.map(recording => {
        // Create a thumbnail URL with the correct path
        return {
          ...recording,
          thumbnailUrl: isProduction ?
            `${window.location.origin}${recording.fileUrl}#t=1` :
            `${SERVER_URL}${recording.fileUrl}#t=1`
        };
      });
      
      setRecordings(processedRecordings);
      } catch (err) {
        console.error('Error fetching recordings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecordings();
  }, [user]);

  const handleDelete = async (recordingId) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${SERVER_URL}/recordings/${recordingId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete recording');
      }

      // Remove the deleted recording from state
      setRecordings(recordings.filter(rec => rec._id !== recordingId));
      
      // Close the player if the deleted recording was being played
      if (selectedRecording && selectedRecording._id === recordingId) {
        setSelectedRecording(null);
      }
    } catch (err) {
      console.error('Error deleting recording:', err);
      alert('Failed to delete recording: ' + err.message);
    }
  };

  const playRecording = (recording) => {
    setSelectedRecording(recording);
    
    // Use setTimeout to ensure the video element is rendered before trying to play
    setTimeout(() => {
      if (videoRef.current) {
        // Use different URL construction for production vs development
        videoRef.current.src = 
          `${SERVER_URL.replace('/api', '')}${recording.fileUrl}`;
        
        videoRef.current.load();
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          console.log('Video source:', videoRef.current.src);
          console.log('Video ready state:', videoRef.current.readyState);
          alert('Failed to play video. Please try again or download the recording.');
        });
      }
    }, 300); // Increased timeout to ensure DOM is updated
  };

  const downloadRecording = (recording) => {
    try {
      // Create a temporary anchor element to trigger download
      const downloadLink = document.createElement('a');
      
      // Use different URL construction for production vs development
      const fileUrl = isProduction ?
        `${window.location.origin}${recording.fileUrl}` :
        `${SERVER_URL.replace('/api', '')}${recording.fileUrl.startsWith('/') ? recording.fileUrl : '/' + recording.fileUrl}`;
      
      console.log('Downloading from URL:', fileUrl); // For debugging
      
      // Use fetch to get the file as a blob
      fetch(fileUrl, {
        method: 'GET',
        headers: {
          'x-auth-token': localStorage.getItem('token')
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(blob => {
        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        downloadLink.href = blobUrl;
        downloadLink.download = recording.title || `recording_${recording._id}.webm`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(downloadLink);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      })
      .catch(error => {
        console.error('Error downloading recording:', error);
        alert('Failed to download recording. Please try again.');
      });
    } catch (err) {
      console.error('Error setting up download:', err);
      alert('Failed to download recording. Please try again.');
    }
  };

  const closePlayer = () => {
    setSelectedRecording(null);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <Container><h2>Loading recordings...</h2></Container>;
  }

  if (error) {
    return (
      <Container>
        <Header>
          <BackButton to="/">
            <FaArrowLeft /> Back to Home
          </BackButton>
          <h1>My Recordings</h1>
        </Header>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton to="/">
          <FaArrowLeft /> Back to Home
        </BackButton>
        <h1>My Recordings</h1>
      </Header>

      {selectedRecording && (
        <VideoPlayerOverlay>
          <VideoPlayerContainer>
            <CloseButton onClick={closePlayer}>
              <FaTimes />
            </CloseButton>
            <h2>{selectedRecording.title}</h2>
            <VideoPlayer 
              ref={videoRef} 
              controls 
              autoPlay
            >
              <source 
                src={isProduction ? 
                  `${window.location.origin}${selectedRecording.fileUrl}` : 
                  `${SERVER_URL.replace('/api', '')}${selectedRecording.fileUrl}`
                } 
                type={selectedRecording.mimeType || 'video/webm'} 
              />
              Your browser does not support the video tag.
            </VideoPlayer>
            <VideoInfo>
              <DownloadButton onClick={() => downloadRecording(selectedRecording)}>
                <FaDownload /> Download Recording
              </DownloadButton>
            </VideoInfo>
          </VideoPlayerContainer>
        </VideoPlayerOverlay>
      )}

      {recordings.length === 0 ? (
        <EmptyState>
          <h3>No recordings found</h3>
          <p>Start a stream and record it to see your recordings here.</p>
        </EmptyState>
      ) : (
        <RecordingsList>
          {recordings.map(recording => (
            <RecordingItem key={recording._id}>
              <RecordingThumbnail 
                imageUrl={recording.thumbnailUrl}
                onClick={() => playRecording(recording)}
              >
                <ThumbnailTitle>{recording.title}</ThumbnailTitle>
              </RecordingThumbnail>
              <RecordingActions>
                <ActionButton onClick={() => playRecording(recording)}>
                  <FaPlay /> Play
                </ActionButton>
                <ActionButton onClick={() => downloadRecording(recording)}>
                  <FaDownload /> Download
                </ActionButton>
                <ActionButton onClick={() => handleDelete(recording._id)} delete>
                  <FaTrash /> Delete
                </ActionButton>
              </RecordingActions>
            </RecordingItem>
          ))}
        </RecordingsList>
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    margin: 0;
  }
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #4267B2;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    background-color: #365899;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: #f0f2f5;
  border-radius: 8px;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const RecordingsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const RecordingItem = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RecordingThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  background-color: #000;
  background-image: url(${props => props.imageUrl || ''});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%);
  }
`;

const ThumbnailTitle = styled.h3`
  color: white;
  margin: 0;
  padding: 1rem;
  position: relative;
  z-index: 1;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const RecordingInfo = styled.div`
  padding: 1rem;
  
  h3 {
    margin-top: 0;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin: 0.25rem 0;
    color: #666;
  }
`;

const RecordingActions = styled.div`
  display: flex;
  border-top: 1px solid #ddd;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  background-color: ${props => props.delete ? '#f44336' : '#4267B2'};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    background-color: ${props => props.delete ? '#d32f2f' : '#365899'};
  }
  
  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.3);
  }
`;

const VideoPlayerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const VideoPlayerContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 80%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  padding: 1.5rem;
  
  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-height: 60vh;
  background-color: #000;
`;

const VideoInfo = styled.div`
  margin-top: 1rem;
  
  p {
    margin: 0.5rem 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
  
  &:hover {
    color: #f44336;
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #4CAF50;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  margin-top: 1rem;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    background-color: #388E3C;
  }
`;

export default Recordings;